/**
 * One job, big buttons. Every action is queued (works offline) and reflected on screen right
 * away; the server's version replaces it when the device next syncs.
 */
import { useEffect, useRef, useState } from 'react';
import { WORK_NOTE_MIN } from '@wo/shared';
import { afterClock, ClockOutSheet, currentClock } from './ClockSheet';
import { newClientId } from './db';
import { enqueueFile, enqueueItem } from './queue';
import { LubeSheet } from './LubeSheet';
import { FieldReportSheet, PartsSheet } from './PartsSheet';
import { RentalSheet } from './RentalSheet';
import { SignSheet } from './SignSheet';
import type { JobsPayload, MechForms, MechJob } from './types';
import { Big, bigInput, NoteBox, Sheet } from './ui';

export function JobScreen({
  job,
  payload,
  userId,
  onBack,
  onLocal,
}: {
  job: MechJob;
  payload: JobsPayload;
  userId: number;
  onBack: () => void;
  /** Apply an optimistic change to the cached payload. */
  onLocal: (fn: (p: JobsPayload) => JobsPayload) => void;
}) {
  const [sheet, setSheet] = useState<null | 'note' | 'issue' | 'meter' | 'hold' | 'ready' | 'parts' | 'fieldReport' | 'lube' | 'rental' | 'sign' | 'clockOut' | 'switch'>(null);
  const [readyNote, setReadyNote] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const clockedHere = payload.activePunch?.workOrderId === job.id;
  const say = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 2500);
  };
  const setJob = (fn: (j: MechJob) => MechJob) => onLocal((p) => ({ ...p, jobs: p.jobs.map((j) => (j.id === job.id ? fn(j) : j)) }));
  const setForms = (fn: (f: MechForms) => MechForms) => setJob((j) => ({ ...j, forms: fn(j.forms) }));

  // Clock Out always asks for the Work Performed note and what's next (another job, lunch, day).
  // Clock In while on another job or shop time asks for the note on what's being left.
  const clock = async () => {
    if (clockedHere) return setSheet('clockOut');
    const cur = currentClock(payload);
    if (cur.state === 'job' || cur.state === 'time') return setSheet('switch');
    await enqueueItem(userId, `Clock in #${job.number}`, { type: 'clock_in', workOrderId: job.id });
    onLocal((p) => afterClock(p, { kind: 'job', job }));
    say('Clocked in');
  };

  const photo = async (file: File, purpose = 'general', noteClientId?: string) => {
    await enqueueFile(userId, `Photo #${job.number}`, file, { workOrderId: job.id, kind: 'PHOTO', purpose, noteClientId });
    setJob((j) => ({ ...j, photos: [...j.photos, { id: -Date.now(), clientId: '', purpose, capturedAt: new Date().toISOString() }] }));
  };

  const canWork = job.status === 'ASSIGNED' || job.status === 'IN_PROGRESS' || job.status === 'ON_HOLD';

  return (
    <div className="mx-auto max-w-3xl p-4 pb-24">
      <button className="mb-2 min-h-[56px] text-lg text-blue-800" onClick={onBack}>← My Jobs</button>
      <div className="mb-4 rounded-2xl bg-white p-4 shadow">
        <div className="flex items-start justify-between gap-2">
          <h1 className="text-3xl font-black">#{job.number}</h1>
          <span className="rounded-full bg-slate-200 px-3 py-1 text-base font-semibold">{job.status.replace(/_/g, ' ').toLowerCase()}</span>
        </div>
        <p className="text-xl font-bold">{job.customer.name}</p>
        <p className="text-lg">{job.machine.make} {job.machine.model} · S/N {job.machine.serialNumber}{job.machine.equipmentNo && ` · EQ# ${job.machine.equipmentNo}`}</p>
        {job.machineLocation && <p className="text-lg">📍 {job.machineLocation}</p>}
        {job.customer.phone && <a className="text-lg text-blue-800" href={`tel:${job.customer.phone}`}>☎ {job.customer.phone}</a>}
        <p className="mt-3 whitespace-pre-wrap rounded-lg bg-amber-50 p-3 text-lg">{job.problemDescription}</p>
        {job.holdReason && <p className="mt-2 text-lg font-bold text-orange-800">On hold: {job.holdReason}{job.holdNote && ` — ${job.holdNote}`}</p>}
        <Crew job={job} />
        <p className="mt-2 text-base text-slate-600">
          Today {job.hoursToday.toFixed(1)} h · Total {job.hoursTotal.toFixed(1)} h · Last hour meter {job.lastHourMeter ?? '—'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Big tone={clockedHere ? 'stop' : 'go'} className="col-span-2 min-h-[80px] text-2xl" onClick={clock} disabled={!canWork}>
          {clockedHere ? 'Clock Out' : 'Clock In'}
        </Big>
        <Big className="col-span-2" onClick={() => setSheet('note')} disabled={!canWork}>🎤 Add Note (talk or type)</Big>
        <PhotoButton label="Take Photo" disabled={!canWork} onPhoto={(f) => photo(f).then(() => say('Photo saved'))} />
        <Big onClick={() => setSheet('meter')} disabled={!canWork}>Hour Meter</Big>
        <Big tone="warn" onClick={() => setSheet('issue')} disabled={!canWork}>Report Additional Issue</Big>
        {job.allowedActions.includes('hold') && <Big onClick={() => setSheet('hold')}>Put On Hold</Big>}
        {job.allowedActions.includes('resume') && (
          <Big onClick={async () => {
            await enqueueItem(userId, `Resume #${job.number}`, { type: 'resume', workOrderId: job.id });
            setJob((j) => ({ ...j, status: 'IN_PROGRESS', holdReason: null }));
          }}>Resume</Big>
        )}
        {job.isLead !== false && <Big tone="go" onClick={() => setSheet('ready')} disabled={job.status !== 'IN_PROGRESS'}>Ready for Review</Big>}
        <Big onClick={() => setSheet('parts')} disabled={!canWork && job.status !== 'READY_FOR_REVIEW'}>
          Parts{job.forms.parts.lines.length ? ` (${job.forms.parts.lines.length})` : job.forms.parts.noPartsUsed ? ' — none used' : ''}
        </Big>
        {/* Only the forms this job type needs (profile jobForms); old cached payloads show both. */}
        {(job.forms.requiredForms ? job.forms.reportTitle : 'Field Report') && (
          <Big onClick={() => setSheet('fieldReport')} disabled={!canWork}>{job.forms.reportTitle ?? 'Field Report'}</Big>
        )}
        {(!job.forms.requiredForms || job.forms.requiredForms.includes('lube') || job.forms.lube) && (
          <Big onClick={() => setSheet('lube')} disabled={!canWork && !job.forms.lube}>
            Lube Report{job.forms.lube?.submittedAt ? ' ✓' : ''}
          </Big>
        )}
        {job.forms.rental && (
          <Big onClick={() => setSheet('rental')}>
            Rental {job.forms.rental.phase === 'OUT' ? 'Check-Out' : 'Check-In'}{(job.forms.rental.phase === 'OUT' ? job.forms.rental.out : job.forms.rental.in)?.submittedAt ? ' ✓' : ''}
          </Big>
        )}
        <Big onClick={() => setSheet('sign')} disabled={!canWork && job.status !== 'READY_FOR_REVIEW'}>
          Signatures{signaturesDone(job.forms) ? ' ✓' : ''}
        </Big>
      </div>

      {job.drafts.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-2xl font-bold">Voice notes to review</h2>
          {job.drafts.map((d, i) => <DraftCard key={d.audioClientId || i} draft={d} userId={userId} voiceEnabled={payload.voiceEnabled} onDone={() => setJob((j) => ({ ...j, drafts: j.drafts.filter((x) => x !== d) }))} />)}
        </section>
      )}

      <section className="mt-6">
        <h2 className="mb-2 text-2xl font-bold">Notes</h2>
        {job.notes.length === 0 && <p className="text-lg text-slate-500">No notes yet.</p>}
        <ul className="space-y-2">
          {job.notes.map((n) => (
            <li key={n.id} className="rounded-xl bg-white p-3 shadow">
              <div className="text-sm font-semibold uppercase text-slate-500">
                {n.kind.replace(/_/g, ' ').toLowerCase()} · {n.by} · {new Date(n.at).toLocaleString()}
                {n.kind === 'ADDITIONAL_ISSUE' && (n.resolved ? ' · handled' : ' · waiting for Manager')}
              </div>
              <p className="whitespace-pre-wrap text-lg">{n.body}</p>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-base text-slate-600">{job.photos.length} photo{job.photos.length === 1 ? '' : 's'} on this job</p>
      </section>

      {sheet === 'note' && <NoteSheet onClose={() => setSheet(null)} onSave={async (body) => {
        await enqueueItem(userId, `Note #${job.number}`, { type: 'note', workOrderId: job.id, kind: 'WORK_PERFORMED', body });
        setJob((j) => ({ ...j, notes: [...j.notes, { id: -Date.now(), kind: 'WORK_PERFORMED', body, by: payload.me.name, at: new Date().toISOString(), correctsNoteId: null, resolved: null }] }));
        setSheet(null);
        say('Note saved');
      }} />}
      {sheet === 'issue' && <IssueSheet onClose={() => setSheet(null)} onSave={async (body, photos) => {
        const noteClientId = newClientId();
        for (const p of photos) await photo(p, 'issue', noteClientId);
        await enqueueItem(userId, `Additional issue #${job.number}`, { type: 'note', workOrderId: job.id, kind: 'ADDITIONAL_ISSUE', body }, noteClientId);
        setJob((j) => ({ ...j, notes: [...j.notes, { id: -Date.now(), kind: 'ADDITIONAL_ISSUE', body, by: payload.me.name, at: new Date().toISOString(), correctsNoteId: null, resolved: false }] }));
        setSheet(null);
        say('Sent to the Manager');
      }} />}
      {sheet === 'meter' && <MeterSheet last={job.lastHourMeter} onClose={() => setSheet(null)} onSave={async (reading, file) => {
        const photoClientId = await enqueueFile(userId, `Hour meter photo #${job.number}`, file, { workOrderId: job.id, kind: 'PHOTO', purpose: 'hour_meter' });
        await enqueueItem(userId, `Hour meter #${job.number}`, { type: 'hour_meter', workOrderId: job.id, reading, photoClientId });
        setJob((j) => ({ ...j, lastHourMeter: reading.toFixed(1) }));
        setSheet(null);
        say('Hour meter saved');
      }} />}
      {sheet === 'hold' && <HoldSheet reasons={payload.holdReasons} needsWorkNote={clockedHere} onClose={() => setSheet(null)} onSave={async (holdReason, reason, workNote) => {
        await enqueueItem(userId, `Hold #${job.number}`, { type: 'hold', workOrderId: job.id, holdReason, reason, ...(clockedHere ? { workNote } : {}) });
        onLocal((p) => {
          const next = clockedHere ? afterClock(p, { kind: 'out', lunch: false }, workNote ? { woId: job.id, body: workNote } : undefined) : p;
          return { ...next, jobs: next.jobs.map((j) => (j.id === job.id ? { ...j, status: 'ON_HOLD', holdReason, holdNote: reason || null } : j)) };
        });
        setSheet(null);
      }} />}
      {(sheet === 'clockOut' || sheet === 'switch') && (
        <ClockOutSheet payload={payload} userId={userId} target={sheet === 'switch' ? job : undefined} onLocal={onLocal} onClose={() => { setSheet(null); say('Saved'); }} />
      )}
      {sheet === 'ready' && (
        <Sheet title="Ready for Review" onClose={() => setSheet(null)}>
          <ul className="mb-4 space-y-2 text-lg">
            {job.checks.map((c) => (
              <li key={c.key} className="flex gap-3">
                <span className={`font-black ${c.ok ? 'text-green-700' : 'text-red-700'}`}>{c.ok ? '✓' : '✗'}</span>
                <span>{c.label}{c.detail && <span className="block text-base text-slate-500">{c.detail}</span>}</span>
              </li>
            ))}
          </ul>
          <p className="mb-4 text-base text-slate-600">The list updates after your work syncs.</p>
          {clockedHere && (
            <div className="mb-3">
              <p className="mb-1 text-lg font-semibold">What did you do? (required to clock out)</p>
              <NoteBox value={readyNote} onChange={(e) => setReadyNote(e.target.value)} placeholder="Work performed" />
            </div>
          )}
          <Big tone="go" className="w-full" disabled={!job.checks.every((c) => c.ok) || (clockedHere && readyNote.trim().length < WORK_NOTE_MIN)} onClick={async () => {
            const body = readyNote.trim();
            if (clockedHere) await enqueueItem(userId, `Clock out #${job.number}`, { type: 'clock_out', workOrderId: job.id, note: body, reason: 'OUT' });
            await enqueueItem(userId, `Ready #${job.number}`, { type: 'ready', workOrderId: job.id });
            onLocal((p) => {
              const next = clockedHere ? afterClock(p, { kind: 'out', lunch: false }, { woId: job.id, body }) : p;
              return { ...next, jobs: next.jobs.map((j) => (j.id === job.id ? { ...j, status: 'READY_FOR_REVIEW' } : j)) };
            });
            setReadyNote('');
            setSheet(null);
          }}>
            {clockedHere ? 'Clock out and send for review' : 'Send for review'}
          </Big>
        </Sheet>
      )}

      {sheet === 'parts' && <PartsSheet job={job} userId={userId} setForms={setForms} onClose={() => setSheet(null)} />}
      {sheet === 'fieldReport' && <FieldReportSheet job={job} lists={payload.formLists} userId={userId} setForms={setForms} onClose={() => { setSheet(null); say(`${job.forms.reportTitle ?? 'Field Report'} saved`); }} />}
      {sheet === 'lube' && <LubeSheet job={job} lists={payload.formLists} userId={userId} setForms={setForms} onClose={() => setSheet(null)} />}
      {sheet === 'rental' && job.forms.rental && <RentalSheet job={job} lists={payload.formLists} userId={userId} setForms={setForms} onClose={() => setSheet(null)} />}
      {sheet === 'sign' && <SignSheet job={job} payload={payload} lists={payload.formLists} userId={userId} setForms={setForms} onClose={() => setSheet(null)} />}

      {toast && <div className="fixed inset-x-4 bottom-6 z-50 rounded-xl bg-slate-900 p-4 text-center text-xl text-white">{toast}</div>}
    </div>
  );
}

/** Technician signed every form, and the customer signed each or "Customer not present" is recorded. */
function signaturesDone(f: MechForms): boolean {
  const sets = f.signatureSets ?? [{ technician: f.signatures.technician, customer: f.signatures.customer }];
  return sets.every((s) => s.technician) && (f.signatures.customerNotPresent || sets.every((s) => s.customer));
}

function PhotoButton({ label, onPhoto, disabled }: { label: string; onPhoto: (f: File) => void; disabled?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <Big onClick={() => ref.current?.click()} disabled={disabled}>{label}</Big>
      {/* capture= opens the camera directly (not the library), so photos keep their EXIF time (D7) */}
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) onPhoto(f);
        e.target.value = '';
      }} />
    </>
  );
}

function NoteSheet({ onClose, onSave }: { onClose: () => void; onSave: (body: string) => void }) {
  const [body, setBody] = useState('');
  return (
    <Sheet title="Work performed" onClose={onClose}>
      <NoteBox value={body} onChange={(e) => setBody(e.target.value)} autoFocus placeholder="What did you do?" />
      <p className="my-2 text-base text-slate-600">Notes can’t be changed after you save. Add a correction if needed.</p>
      <Big tone="go" className="w-full" disabled={!body.trim()} onClick={() => onSave(body.trim())}>Save Note</Big>
    </Sheet>
  );
}

function IssueSheet({ onClose, onSave }: { onClose: () => void; onSave: (body: string, photos: File[]) => void }) {
  const [body, setBody] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  return (
    <Sheet title="Report Additional Issue" onClose={onClose}>
      <p className="mb-2 text-lg">Found more work? Describe it — the Manager decides what to do.</p>
      <NoteBox value={body} onChange={(e) => setBody(e.target.value)} autoFocus placeholder="What did you find?" />
      <div className="my-3 flex items-center gap-3">
        <PhotoButton label="Add Photo" onPhoto={(f) => setPhotos((p) => [...p, f])} />
        <span className="text-lg">{photos.length} photo(s)</span>
      </div>
      <Big tone="warn" className="w-full" disabled={!body.trim()} onClick={() => onSave(body.trim(), photos)}>Send to Manager</Big>
    </Sheet>
  );
}

function MeterSheet({ last, onClose, onSave }: { last: string | null; onClose: () => void; onSave: (reading: number, photo: File) => void }) {
  const [value, setValue] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const n = Number(value);
  const lower = last !== null && value !== '' && n < Number(last);
  return (
    <Sheet title="Hour meter" onClose={onClose}>
      <p className="mb-2 text-lg">Last reading: {last ?? 'none'}</p>
      <input inputMode="decimal" className={bigInput} placeholder="Reading" value={value} onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ''))} />
      {lower && <p className="mt-2 rounded-lg bg-red-100 p-3 text-lg text-red-900">That's lower than the last reading. Check the meter — only the Manager can enter a lower number.</p>}
      <div className="my-3 flex items-center gap-3">
        <PhotoButton label={file ? 'Retake Photo' : 'Photo of Meter'} onPhoto={setFile} />
        <span className="text-lg">{file ? '✓ photo taken' : 'photo required'}</span>
      </div>
      <Big tone="go" className="w-full" disabled={!file || value === '' || !Number.isFinite(n) || lower} onClick={() => file && onSave(n, file)}>Save Reading</Big>
    </Sheet>
  );
}

function HoldSheet({ reasons, needsWorkNote, onClose, onSave }: { reasons: string[]; needsWorkNote: boolean; onClose: () => void; onSave: (reason: string, note: string, workNote: string) => void }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [workNote, setWorkNote] = useState('');
  return (
    <Sheet title="Put On Hold" onClose={onClose}>
      <div className="mb-3 grid gap-2">
        {reasons.map((r) => (
          <Big key={r} tone={reason === r ? 'go' : 'neutral'} onClick={() => setReason(r)}>{r}</Big>
        ))}
      </div>
      <NoteBox className="min-h-[120px]" placeholder={reason === 'Other' ? 'Describe the reason (required)' : 'Note (optional)'} value={note} onChange={(e) => setNote(e.target.value)} />
      {needsWorkNote && (
        <div className="mt-3">
          <p className="mb-1 text-lg font-semibold">What did you do? (required to clock out)</p>
          <NoteBox className="min-h-[120px]" hint={false} placeholder="Work performed" value={workNote} onChange={(e) => setWorkNote(e.target.value)} />
        </div>
      )}
      <Big tone="warn" className="mt-3 w-full" disabled={!reason || (reason === 'Other' && note.trim().length < 3) || (needsWorkNote && workNote.trim().length < WORK_NOTE_MIN)} onClick={() => onSave(reason, note.trim(), workNote.trim())}>
        Put On Hold (clocks you out)
      </Big>
    </Sheet>
  );
}

function DraftCard({ draft, userId, voiceEnabled, onDone }: { draft: JobsPayload['jobs'][number]['drafts'][number]; userId: number; voiceEnabled: boolean; onDone: () => void }) {
  const t = draft.transcription;
  const [body, setBody] = useState(t?.cleanNote ?? '');
  // The transcript may arrive after this card is on screen; fill it in unless they've started typing.
  useEffect(() => {
    if (t?.cleanNote) setBody((b) => b || t.cleanNote!);
  }, [t?.cleanNote]);
  if (!draft.audioClientId) return <div className="mb-2 rounded-xl bg-white p-3 text-lg shadow">Recording waiting to upload…</div>;
  const status = !t || t.status === 'PENDING' ? 'Transcription pending…' : t.status === 'FAILED' ? 'Transcription failed — please type the note.' : t.status === 'DISABLED' || !voiceEnabled ? 'Type what you said:' : 'Check the text, fix anything wrong, then submit:';
  return (
    <div className="mb-3 rounded-xl bg-white p-3 shadow">
      <p className="mb-2 text-lg font-semibold">{status}</p>
      <NoteBox value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Big tone="go" disabled={!body.trim()} onClick={async () => {
          await enqueueItem(userId, 'Submit voice note', { type: 'submit_draft', audioClientId: draft.audioClientId, body: body.trim() });
          onDone();
        }}>Submit</Big>
        <Big onClick={async () => {
          await enqueueItem(userId, 'Discard voice note', { type: 'discard_draft', audioClientId: draft.audioClientId });
          onDone();
        }}>Discard</Big>
      </div>
    </div>
  );
}

/** Who else is on the job (D51). Helpers see who leads it; the lead marks it Ready for Review. */
function Crew({ job }: { job: MechJob }) {
  if (job.isLead === false) return <p className="mt-2 text-lg text-slate-700">Helping {job.lead ?? 'the lead'}{job.helpers?.length ? ` (with ${job.helpers.join(', ')})` : ''}. {job.lead ?? 'The lead'} marks it Ready for Review.</p>;
  if (job.helpers?.length) return <p className="mt-2 text-lg text-slate-700">Also on this job: {job.helpers.join(', ')}</p>;
  return null;
}
